import React, { useEffect, useState } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_API_BASE_URL;

/* ─── Design tokens: Blue · White · Light Gray ───────────────────────────── */
const t = {
  /* surfaces */
  white: "#FFFFFF",
  pageBg: "#F4F6F9",
  surfaceHover: "#EEF2F8",
  headerBg: "#F8FAFD",

  /* borders */
  borderLight: "#DDE3EE",
  borderFocus: "#3B7EF6",

  /* blue accent */
  accent: "#2563EB",
  accentHover: "#1D55D4",
  accentSoft: "rgba(37,99,235,0.08)",
  accentGlow: "rgba(37,99,235,0.18)",
  accentLight: "#EEF3FF",

  /* danger */
  danger: "#DC2626",
  dangerSoft: "rgba(220,38,38,0.07)",

  /* text */
  textPrimary: "#111827",
  textSecondary: "#4B5563",
  textMuted: "#9CA3AF",
  textOnAccent: "#FFFFFF",

  /* tags */
  daily: { bg: "#EEF3FF", text: "#2563EB", border: "#BFCFFB" },
  weekly: { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" },

  /* shape */
  radius: "10px",
  radiusSm: "7px",
  radiusXs: "5px",

  /* type */
  fontSans: "'Plus Jakarta Sans', 'Outfit', sans-serif",
  fontMono: "'DM Mono', 'Fira Mono', monospace",
};

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const s = {
  wrapper: {
    width: "100%",
    background: t.white,
    border: `1px solid ${t.borderLight}`,
    borderRadius: "14px",
    overflow: "hidden",
    fontFamily: t.fontSans,
    boxShadow: "0 1px 4px rgba(17,24,39,0.06), 0 4px 20px rgba(37,99,235,0.06)",
  },

  /* Header */
  header: {
    padding: "18px 22px",
    borderBottom: `1px solid ${t.borderLight}`,
    background: t.headerBg,
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  headerIconWrap: {
    width: 32,
    height: 32,
    borderRadius: t.radiusSm,
    background: t.accentLight,
    border: `1px solid ${t.borderFocus}22`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: t.textPrimary,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  headerSub: {
    fontSize: 12,
    color: t.textMuted,
    margin: 0,
    marginTop: 1,
    fontWeight: 400,
  },
  headerCount: {
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: 600,
    color: t.accent,
    background: t.accentLight,
    padding: "3px 10px",
    borderRadius: "20px",
    border: `1px solid ${t.borderFocus}33`,
    fontFamily: t.fontMono,
    letterSpacing: "0.02em",
  },

  /* Body */
  body: {
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    background: t.pageBg,
    minHeight: 52,
  },

  /* Empty state */
  emptyState: {
    padding: "32px 0 28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
  emptyCircle: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: t.white,
    border: `1.5px dashed ${t.borderLight}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: 13,
    color: t.textMuted,
    fontWeight: 500,
    margin: 0,
  },
  emptyHint: {
    fontSize: 12,
    color: t.textMuted,
    opacity: 0.7,
    margin: 0,
  },

  /* Question row */
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "11px 14px",
    background: t.white,
    border: `1px solid ${t.borderLight}`,
    borderRadius: t.radius,
    cursor: "default",
    transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
  },
  rowHover: {
    borderColor: "#C7D4F0",
    boxShadow: "0 2px 8px rgba(37,99,235,0.07)",
    background: "#FAFCFF",
  },
  rowNum: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: t.pageBg,
    border: `1px solid ${t.borderLight}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 700,
    color: t.textMuted,
    flexShrink: 0,
    fontFamily: t.fontMono,
  },
  rowText: {
    flex: 1,
    fontSize: 13.5,
    fontWeight: 500,
    color: t.textPrimary,
    lineHeight: 1.5,
    letterSpacing: "-0.005em",
    margin: 0,
  },
  tag: (type) => ({
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    padding: "3px 9px",
    borderRadius: "20px",
    background: type === "daily" ? t.daily.bg : t.weekly.bg,
    color: type === "daily" ? t.daily.text : t.weekly.text,
    border: `1px solid ${type === "daily" ? t.daily.border : t.weekly.border}`,
    fontFamily: t.fontMono,
    flexShrink: 0,
  }),
  deleteBtn: {
    width: 28,
    height: 28,
    borderRadius: t.radiusXs,
    border: `1px solid transparent`,
    background: "transparent",
    color: t.textMuted,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.14s, color 0.14s, border-color 0.14s",
    padding: 0,
  },
  deleteBtnHover: {
    background: t.dangerSoft,
    color: t.danger,
    borderColor: `rgba(220,38,38,0.15)`,
  },

  /* Footer */
  footer: {
    padding: "13px 14px 14px",
    borderTop: `1px solid ${t.borderLight}`,
    background: t.white,
  },
  inputRow: {
    display: "flex",
    gap: "7px",
    alignItems: "center",
  },
  textInput: {
    flex: 1,
    height: 38,
    background: t.pageBg,
    border: `1px solid ${t.borderLight}`,
    borderRadius: t.radiusSm,
    padding: "0 12px",
    fontSize: 13.5,
    color: t.textPrimary,
    outline: "none",
    fontFamily: t.fontSans,
    transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
    minWidth: 0,
    fontWeight: 450,
  },
  textInputFocus: {
    background: t.white,
    borderColor: t.borderFocus,
    boxShadow: `0 0 0 3px ${t.accentSoft}`,
  },
  select: {
    height: 38,
    background: t.pageBg,
    border: `1px solid ${t.borderLight}`,
    borderRadius: t.radiusSm,
    padding: "0 28px 0 11px",
    fontSize: 12.5,
    color: t.textSecondary,
    outline: "none",
    fontFamily: t.fontMono,
    fontWeight: 500,
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%234B5563'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 9px center",
    width: 92,
    transition: "border-color 0.15s, box-shadow 0.15s",
    flexShrink: 0,
  },
  selectFocus: {
    borderColor: t.borderFocus,
    boxShadow: `0 0 0 3px ${t.accentSoft}`,
  },
  addBtn: {
    height: 38,
    padding: "0 16px",
    background: t.accent,
    color: t.textOnAccent,
    border: "none",
    borderRadius: t.radiusSm,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: t.fontSans,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    letterSpacing: "0.01em",
    transition: "background 0.15s, box-shadow 0.15s, transform 0.1s",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  addBtnHover: {
    background: t.accentHover,
    boxShadow: `0 4px 14px ${t.accentGlow}`,
    transform: "translateY(-1px)",
  },
  addBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    boxShadow: "none",
    transform: "none",
  },

  /* Loading */
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 0",
    gap: 10,
    color: t.textMuted,
    fontSize: 13,
    fontWeight: 500,
  },

  /* Error */
  errorWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "16px 0 12px",
  },
  errorText: {
    fontSize: 13,
    color: t.danger,
    fontWeight: 500,
    margin: 0,
  },
};

/* ─── Spinner ────────────────────────────────────────────────────────────── */
const Spinner = ({ size = 16, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    style={{ animation: "yn-spin 0.75s linear infinite", flexShrink: 0 }}
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const QuestionIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={t.accent}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4h6v2" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={t.danger}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/* ─── useHover ───────────────────────────────────────────────────────────── */
function useHover() {
  const [h, setH] = useState(false);
  return [
    h,
    { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) },
  ];
}

/* ─── QuestionRow ────────────────────────────────────────────────────────── */
const QuestionRow = ({ question, index, isDeleting, onDelete }) => {
  const [rHov, rProps] = useHover();
  const [bHov, bProps] = useHover();

  return (
    <div
      className="yn-row"
      {...rProps}
      style={{
        ...s.row,
        ...(rHov ? s.rowHover : {}),
        opacity: isDeleting ? 0.45 : 1,
      }}
    >
      <span style={s.rowNum}>{index + 1}</span>
      <p style={s.rowText}>{question.question_text}</p>
      <span style={s.tag(question.type)}>
        {question.type === "daily" ? "Daily" : "Weekly"}
      </span>
      <button
        onClick={onDelete}
        disabled={isDeleting}
        title="Remove question"
        {...bProps}
        style={{ ...s.deleteBtn, ...(bHov ? s.deleteBtnHover : {}) }}
      >
        {isDeleting ? <Spinner size={13} color={t.danger} /> : <TrashIcon />}
      </button>
    </div>
  );
};

/* ─── Main component ─────────────────────────────────────────────────────── */
const YesNoQuestionsManager = ({ user_id, store_id }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newQuestionText, setNewText] = useState("");
  const [newQuestionType, setNewType] = useState("daily");
  const [inputFocused, setInputFocused] = useState(false);
  const [selectFocused, setSelectFocused] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [addHov, addHovProps] = useHover();
  // Folded by default. Opens by itself only when the user already has
  // questions assigned; otherwise the head expands it by hand.
  const [expanded, setExpanded] = useState(false);

  const fetchAssignedQuestions = async ({ syncExpanded = false } = {}) => {
    if (!user_id) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BASE}/api/yes-no/user-questions`, {
        params: { store_id, user_id },
      });
      if (res.data.success) {
        setQuestions(res.data.data);
        // Only on the first load for a user — a refetch after adding or
        // removing a question must not fold the card under the head.
        if (syncExpanded) setExpanded(res.data.data?.length > 0);
      } else setError(res.data.statusMessage || "Failed to fetch questions");
    } catch {
      setError("Error fetching questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setExpanded(false);
    fetchAssignedQuestions({ syncExpanded: true });
  }, [user_id, store_id]);

  const handleAddQuestion = async () => {
    if (!newQuestionText.trim() || addLoading) return;
    setAddLoading(true);
    try {
      const res = await axios.post(`${BASE}/api/yes-no/user-questions`, {
        store_id,
        user_id,
        questions: [
          { question_text: newQuestionText.trim(), type: newQuestionType },
        ],
      });
      if (res.data.success) {
        fetchAssignedQuestions();
        setNewText("");
        setNewType("daily");
      } else {
        alert(res.data.statusMessage || "Failed to add question");
      }
    } catch {
      alert("Error adding question");
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveQuestion = async (question_id) => {
    if (!window.confirm("Remove this question?")) return;
    setDeletingIds((p) => new Set([...p, question_id]));
    try {
      const res = await axios.delete(`${BASE}/api/yes-no/unassign-question`, {
        data: { store_id, user_id, question_id },
      });
      if (res.data.success) fetchAssignedQuestions();
      else alert(res.data.statusMessage || "Failed to remove question");
    } catch {
      alert("Error removing question");
    } finally {
      setDeletingIds((p) => {
        const s = new Set(p);
        s.delete(question_id);
        return s;
      });
    }
  };

  if (!user_id) return null;

  const canAdd = !!newQuestionText.trim() && !addLoading;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;600&display=swap');
        @keyframes yn-spin    { to { transform: rotate(360deg); } }
        @keyframes yn-fadein  { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
        .yn-row               { animation: yn-fadein 0.2s ease both; }
        .yn-row:nth-child(1)  { animation-delay: 0.00s; }
        .yn-row:nth-child(2)  { animation-delay: 0.04s; }
        .yn-row:nth-child(3)  { animation-delay: 0.08s; }
        .yn-row:nth-child(4)  { animation-delay: 0.12s; }
        .yn-row:nth-child(5)  { animation-delay: 0.15s; }
        .yn-row:nth-child(n+6){ animation-delay: 0.18s; }
        input::placeholder    { color: #9CA3AF; }
      `}</style>

      <div style={s.wrapper}>
        {/* ── Header ── */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((p) => !p)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded((p) => !p);
            }
          }}
          style={{
            ...s.header,
            cursor: "pointer",
            userSelect: "none",
            ...(expanded ? {} : { borderBottom: "none" }),
          }}
        >
          <div style={s.headerIconWrap}>
            <QuestionIcon />
          </div>
          <div>
            <p style={s.headerTitle}>Yes / No Questions</p>
            <p style={s.headerSub}>Assigned check-in questions</p>
          </div>
          {!loading && (
            <span style={s.headerCount}>
              {questions.length}{" "}
              {questions.length === 1 ? "question" : "questions"}
            </span>
          )}
          <svg
            aria-hidden="true"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={t.textSecondary}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              flexShrink: 0,
              marginLeft: loading ? "auto" : 0,
              transition: "transform 0.2s",
              transform: expanded ? "rotate(180deg)" : "none",
            }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>

        {expanded && (
        <>
        {/* ── Body ── */}
        <div style={s.body}>
          {loading ? (
            <div style={s.loadingWrap}>
              <Spinner size={18} color={t.accent} />
              <span style={{ color: t.textSecondary }}>Loading questions…</span>
            </div>
          ) : error ? (
            <div style={s.errorWrap}>
              <ErrorIcon />
              <p style={s.errorText}>{error}</p>
            </div>
          ) : questions.length === 0 ? (
            <div style={s.emptyState}>
              <div style={s.emptyCircle}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={t.textMuted}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </div>
              <p style={s.emptyText}>No questions assigned yet</p>
              <p style={s.emptyHint}>
                Use the form below to add your first question
              </p>
            </div>
          ) : (
            questions.map((q, i) => (
              <QuestionRow
                key={q.id}
                question={q}
                index={i}
                isDeleting={deletingIds.has(q.id)}
                onDelete={() => handleRemoveQuestion(q.id)}
              />
            ))
          )}
        </div>

        {/* ── Footer / Add form ── */}
        <div style={s.footer}>
          <div style={s.inputRow}>
            <input
              type="text"
              placeholder="Type a new question…"
              value={newQuestionText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddQuestion()}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              style={{
                ...s.textInput,
                ...(inputFocused ? s.textInputFocus : {}),
              }}
            />
            <select
              value={newQuestionType}
              onChange={(e) => setNewType(e.target.value)}
              onFocus={() => setSelectFocused(true)}
              onBlur={() => setSelectFocused(false)}
              style={{ ...s.select, ...(selectFocused ? s.selectFocus : {}) }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            <button
              onClick={handleAddQuestion}
              disabled={!canAdd}
              {...addHovProps}
              style={{
                ...s.addBtn,
                ...(addHov && canAdd ? s.addBtnHover : {}),
                ...(!canAdd ? s.addBtnDisabled : {}),
              }}
            >
              {addLoading ? <Spinner size={14} color="#fff" /> : <PlusIcon />}
              Add
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </>
  );
};

export default YesNoQuestionsManager;
